import PropTypes from "prop-types";
import React from "react";
import {
  formatCourtedCurrency,
  formatCourtedDecimal,
  formatTenure,
} from "~/helpers/courtedFormatters";
import { formatDateTime, formatNumber } from "~/helpers/formatHelper";
import { BooleanBadge } from "./BooleanBadge";
import { ColorLegendTooltip } from "./ColorLegendTooltip";
import { COURTED_ATTRIBUTES_SHAPE, KIND } from "./courtedConstants";
import { FieldRow } from "./FieldRow";
import { SectionHeader } from "./SectionHeader";

// Render strategies — one per field kind. Each takes the raw value and the
// resolved field config. Returning null hides the value cell (keeps the row).
const renderers = {
  [KIND.text]: (value) => value,
  [KIND.currency]: (value) => formatCourtedCurrency(value),
  [KIND.decimal]: (value) => formatCourtedDecimal(value),
  [KIND.int]: (value) => (value == null ? null : formatNumber(Number(value))),
  [KIND.tenure]: (value) => formatTenure(value),
  [KIND.date]: (value) => formatDateTime(value, "MM/DD/YYYY h:mm A", ""),
  [KIND.bool]: (value, field) => <BooleanBadge value={value} field={field} />,
};

const resolveTitleTooltip = (def) => {
  if (def.titleTooltipKey === "colorLegend") {
    return <ColorLegendTooltip />;
  }
  return undefined;
};

export const Section = ({ def, attrs }) => (
  <>
    <SectionHeader title={def.title} tooltip={resolveTitleTooltip(def)} />
    {def.fields.map((field) => {
      const value = attrs[field.key];
      // Boolean rows are pure signals — hide the entire row when value is
      // missing; other kinds keep the labeled row with an empty value cell.
      if (field.kind === KIND.bool && value == null) {
        return null;
      }
      const rendered = renderers[field.kind](value, field.booleanField);
      return (
        <FieldRow key={field.key} label={field.label} tooltip={field.tooltip}>
          {rendered}
        </FieldRow>
      );
    })}
  </>
);

Section.propTypes = {
  def: PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    titleTooltipKey: PropTypes.string,
    fields: PropTypes.array.isRequired,
  }).isRequired,
  attrs: COURTED_ATTRIBUTES_SHAPE.isRequired,
};

export default Section;
